import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import BackButton from "../../../common/BackButton";
import {
  drawCharts,
  getChartTableInfo,
  ChartSize,
  createChartTableStyle,
} from "../common/chart";
import AuthService from "../../../../../common/AuthService";
import {
  getTestModel,
  ITestModelResult,
} from "../../../../../webapi/service/getTestModel";
import { ChartList } from "../../../../../common/constants";
import { getFiles, IProtRow } from "../../../../../webapi/service/getFIles";
import "../../../../main.css";
import "../../projectdetail.css";
import "./model.css";

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
}

interface IState {
  okfiles: ITestModelResult[];
  ngfiles: ITestModelResult[];
  prots: IProtRow[];
}

class ResultTestModelPanel extends React.Component<IProps, IState> {
  private readonly chartid_fmt = "chart_result_test_model_";
  private readonly DefaultChartSize: ChartSize = {
    width: ChartList.RESULT_TEST_MODEL_WIDTH,
    height: ChartList.RESULT_TEST_MODEL_HEIGHT,
  };
  private readonly ChartListThreshold = ChartList.CHART_LIST_THRESHOLD;
  private _charts: C3.ChartAPI[] | null = null;
  private _chartSize: ChartSize = this.DefaultChartSize;

  constructor(props: IProps) {
    super(props);
    this.state = { okfiles: [], ngfiles: [], prots: [] };

    this.chartSize = this.chartSize.bind(this);
  }

  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  createCharts(prots: IProtRow[]): void {
    const { chartWidth, chartHeight } = getChartTableInfo(
      this.props.mainSpaceWidth,
      this.DefaultChartSize,
      true
    );
    this._charts = drawCharts(prots, this.chartid_fmt, chartWidth, chartHeight);
    this.chartSize({ width: chartWidth, height: chartHeight });
  }

  destroyCharts(): void {
    if (this._charts) {
      this._charts.forEach((chart) => {
        chart.destroy();
      });
      this._charts = null;
    }
  }

  loadTestModel(loading: boolean) {
    const { authService, projectid, onLoading, onCommunicationError } =
      this.props;
    onLoading(loading);
    getTestModel({
      authService: authService,
      projectid: projectid,
      success: (okfiles, ngfiles) => {
        const fileids = okfiles.concat(ngfiles).map((file) => {
          return file.fileid;
        });
        if (fileids.length > 0) {
          getFiles({
            authService: authService,
            projectid: projectid,
            threshold: this.ChartListThreshold,
            fileids: fileids,
            success: (rows, prots) => {
              this.setState(
                { okfiles: okfiles, ngfiles: ngfiles, prots: prots },
                () => {
                  this.createCharts(prots);
                  onLoading(false);
                }
              );
            },
            fail: (id, status) => {
              onCommunicationError(id, status);
            },
          });
        } else {
          this.setState(
            { okfiles: okfiles, ngfiles: ngfiles, prots: [] },
            () => {
              onLoading(false);
            }
          );
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  componentDidMount() {
    this.loadTestModel(true);
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.projectid !== prevProps.projectid) {
      this.setState({ okfiles: [], ngfiles: [], prots: [] });
      this.destroyCharts();
      this.loadTestModel(true);
    }
  }

  componentWillUnmount() {
    this.destroyCharts();
  }

  createCharTable(chartWidth: number) {
    const { t } = this.props;
    const { okfiles, ngfiles, prots } = this.state;
    const files = okfiles.concat(ngfiles);
    const charts: JSX.Element[] = [];
    files.forEach((file, i) => {
      const prot = prots.find((prot) => {
        return prot.fileid === file.fileid;
      });
      if (prot) {
        const chartid = this.chartid_fmt + String(prot.id);
        const label = okfiles.find((ok) => {
          return ok.fileid === file.fileid;
        })
          ? "OK"
          : "NG";
        const result = file.result ? "OK" : "NG";
        const judgement =
          label === result ? "model_result_match" : "model_result_conflict";
        const grid =
          label === result
            ? "result-test-grid-match"
            : "result-test-grid-conflict";
        const health = (Math.round(file.health * 1000) / 1000).toFixed(3);
        charts.push(
          <div className="chart-list-item" key={i}>
            <div id={chartid}></div>
            <div className={grid}>
              <div className="item1" style={{ width: chartWidth }}>
                {prot.fname}
              </div>
              <div className="item2">
                {t("model_result_label")} : {label}
              </div>
              <div className="item3">
                {t("model_result_aijudge")} : {result}
              </div>
              <div className="item4">
                {t("model_result_healthscore")} : {health}
              </div>
              <div className="item5">{t(judgement)}</div>
            </div>
          </div>
        );
      }
    });
    return charts;
  }

  render() {
    const { mainSpaceWidth, onBack, t } = this.props;
    const chartTableStyle = createChartTableStyle(
      mainSpaceWidth,
      this.DefaultChartSize,
      this._charts,
      this.chartSize
    );
    const contents = this.createCharTable(this.chartSize().width);
    return (
      <div className="mainspace">
        <div className="mainspace-title">
          <h3>{t("model_result")}</h3>
        </div>
        <div className="mainspace-toolbar">
          <BackButton onClick={onBack} />
        </div>
        <div className="chart-list-area">
          <div style={chartTableStyle}>{contents}</div>
        </div>
      </div>
    );
  }
}

/**
 * モデルテスト結果画面
 */
export default withTranslation()(ResultTestModelPanel);
